import { EmployeeRestaurant } from '@/contexts/user-context';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import {
  INITIAL_SMART_ORDER_QUERY,
  SMART_ORDER_RESULTS_QUERY,
} from '@/contexts/smart-order-queries';
import {
  SmartOrder_RegenerateMutation,
  SmartOrder_RegenerateMutationVariables,
} from '@/types';
import { useCallback } from 'react';
import { VARIANTS_PACK_FRAGMENT } from '@/contexts/items-context-queries';

export enum RefetchQueryType {
  OPERATOR = 'OPERATOR',
  SUPPLIER = 'SUPPLIER',
}
export interface RegenerateSmartOrderParams {
  employeeRestaurant: EmployeeRestaurant;
  locale: string;
  skipGroupMemberships: boolean;
  skipSaltApi: boolean;
  smartOrderUUIDs: string[];
}

export const REGENERATE_SMART_ORDER = gql`
  mutation SmartOrder_Regenerate(
    $locale: String!
    $restaurantUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $smartOrderUUIDs: [uuid!]!
    $supplierUUID: uuid!
  ) {
    regenerateSmartOrders(
      smart_order_ids: $smartOrderUUIDs
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      smart_order {
        restaurant {
          name
          uuid
        }
        smart_order_results {
          quantity
          search_term
          variant_pack {
            ...ItemsContext_VariantsPackFields
          }
          uuid
        }
        uuid
      }
    }
  }

  ${VARIANTS_PACK_FRAGMENT}
`;

export const useRegenerateSmartOrder = () => {
  const [regenerateSmartOrders, { loading }] = useMutation<
    SmartOrder_RegenerateMutation,
    SmartOrder_RegenerateMutationVariables
  >(REGENERATE_SMART_ORDER, {
    context: {
      noBatch: true,
    },
  });

  const regenerateSmartOrderCallback = useCallback(
    async (
      params: RegenerateSmartOrderParams,
      refetchQueryType?: RefetchQueryType,
    ) => {
      const {
        locale,
        employeeRestaurant,
        skipGroupMemberships,
        skipSaltApi,
        smartOrderUUIDs,
      } = params;

      const refetchQueries =
        refetchQueryType === RefetchQueryType.OPERATOR
          ? [
              getOperationName(INITIAL_SMART_ORDER_QUERY) as string,
              getOperationName(SMART_ORDER_RESULTS_QUERY) as string,
            ]
          : null;

      await regenerateSmartOrders({
        ...(refetchQueries ? { refetchQueries } : {}),
        variables: {
          locale,
          restaurantUUID: employeeRestaurant.uuid,
          skipGroupMemberships,
          skipLiveSalt: skipSaltApi,
          skipSaltApi,
          smartOrderUUIDs,
          supplierUUID: employeeRestaurant.supplierUUID,
          useSaltCache: false,
        },
      });
    },
    [regenerateSmartOrders],
  );

  return {
    regenerateSmartOrders: regenerateSmartOrderCallback,
    regenerateSmartOrdersLoading: loading,
  };
};

export type RegenerateSmartOrders = ReturnType<
  typeof useRegenerateSmartOrder
>['regenerateSmartOrders'];
